import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Tooltip,
  TextField,
  MenuItem,
  FormControl,
  OutlinedInput,
  Select,
  Checkbox,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import SearchIcon from '@mui/icons-material/Search';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ShopContext } from 'Context/ShopContext';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { AlertMessagesContext } from 'react-alert-messages';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { saveAs } from 'file-saver';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import PrintIcon from '@mui/icons-material/Print';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import LinkIcon from '@mui/icons-material/Link';
import { useHistory } from 'react-router-dom';
import { ROUTES, SelectFilterStyle, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { useTranslation } from 'react-i18next';
import CustomerService from 'services/CustomerService';

function CustomerPaymentReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');

  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const history = useHistory();

  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [filterGroups, setFilterGroups] = useState([]);

  const [customerGroups, setCustomerGroups] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [payments, setPayments] = useState([]);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [paymentType, setPaymentType] = useState('PAYMENT');
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    paymentType: 'PAYMENT',
    filterGroups,
  });
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    customer: 'Customer',
    date: {
      title: tCommon('Date'),
      getValue: (datum) => DateTimeHelpers.getDateYMD(new Date(datum.time)),
    },
    time: {
      title: tCommon('Time'),
      getValue: (datum) => DateTimeHelpers.get12HourTime(datum.time),
    },
    customer_group: 'Group',
    credit: 'Credit',
    debit: 'Debit',
    balance: 'Balance',
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(payments, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `CUSTOMER-PAYMENT-REPORT-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const _getPaymentsFiltered = (payments, searchKey) => {
    if (!searchKey) {
      return payments;
    }

    const searchKeySmallCase = searchKey.toLowerCase();
    return (payments || []).filter(
      (payment) =>
        payment?.customer.toLowerCase().includes(searchKeySmallCase) || payment.date.includes(searchKeySmallCase)
    );
  };

  const _getPaymentReportSummary = (payments) => {
    let totalCredit = 0;
    let totalDebit = 0;
    let totalBalance = 0;

    payments.forEach((payment) => {
      totalCredit += parseFloat(payment.credit) || 0;
      totalDebit += parseFloat(payment.debit) || 0;
      totalBalance += parseFloat(payment.balance) || 0;
    });

    return {
      totalCredit: ShopsHelper.getAmountFormatted(shop, totalCredit),
      totalDebit: ShopsHelper.getAmountFormatted(shop, totalDebit),
      totalBalance: ShopsHelper.getAmountFormatted(shop, totalBalance),
    };
  };

  const redirectToVendorDetails = (customer) => {
    history.push(ROUTES.CUSTOMER_DETAILS.replace(':customerId', customer.id));
  };

  const headerData = [
    {
      label: tCommon('Customer'),
      id: 'customer',
      type: 'callback',
      viewRender: (item) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{item?.customer ?? '-'}</span>
          {item?.customerId && (
            <Tooltip title={tCommon('View Customer')}>
              <LinkIcon
                style={{ cursor: 'pointer', marginLeft: 4 }}
                onClick={() => redirectToVendorDetails(item.customerId)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      label: tCommon('Date'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.convertDateToIsoDMY(item.time),
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.get12HourTime(item.time),
    },
    {
      label: tCommon('Group'),
      id: 'customer_group',
      type: 'text',
    },
    {
      label: tCommon('Debit'),
      id: 'credit',
      type: 'text',
    },
    {
      label: tCommon('Credit'),
      id: 'debit',
      type: 'text',
    },
    {
      label: tCommon('Balance'),
      id: 'balance',
      type: 'floatAmount',
    },
  ];

  const getCustomerGroups = async () => {
    const res = await CustomerService.getCustomerGroups();
    setCustomerGroups(res);
  };

  const generatePaymentHistory = () => {
    const { startDate, endDate } = dynamicDateRange('CUSTOMER_PAYMENT_SUMMARY');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    getPayments({
      from_date: DateTimeHelpers.getDateYMD(startDate),
      to_date: DateTimeHelpers.getDateYMD(endDate),
      group_ids: filterGroups,
      payment_type: paymentType,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      paymentType,
      filterGroups,
    });
  };

  const getPayments = useCallback(
    async (customDateParams) => {
      setLoadingIndicator(true);
      try {
        const data = customDateParams;

        const res = await CustomerService.getCustomerPaymentReport(data);
        setPayments(res.data);
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [postAlertMessage]
  );

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      paymentType !== prevFilterData.paymentType ||
      filterGroups !== prevFilterData.filterGroups
    );
  }, [
    filterFromDate,
    filterToDate,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    paymentType,
    prevFilterData.paymentType,
    filterGroups,
    prevFilterData.filterGroups,
  ]);

  useEffect(() => {
    getCustomerGroups();
    generatePaymentHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Customer Payment Report'),
      filterFromDate,
      filterToDate,
      headerData,
      paymentsToDisplay,
      {
        [tCommon('Total Debit')]: `${shop?.currency || ''} ${summary.totalCredit}`,
        [tCommon('Total Credit')]: `${shop?.currency || ''} ${summary.totalDebit}`,
        [tCommon('Total Balance')]: `${shop?.currency || ''} ${summary.totalBalance}`,
      }
    );
  };

  const paymentsToDisplay = _getPaymentsFiltered(payments, searchText);
  const summary = _getPaymentReportSummary(payments);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>Print Report</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>Export As</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a', marginRight: 5 }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            Csv
          </Button>
        </div>
      </div>

      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('Customer Payment Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tCommon('Search purchase')}
              />
              <Tooltip title="Search" placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="CUSTOMER_PAYMENT_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Type')}</label>
            </div>
            <div>
              <TextField
                select
                name="billNo"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (selected === null) {
                      return <span style={{ color: '#888', fontStyle: 'italic' }}>{tCommon('All')}</span>;
                    }
                    return selected;
                  },
                }}
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 135 }}
                value={paymentType}
                onChange={(event) => {
                  setPaymentType(event.target.value);
                }}
                displayEmpty
              >
                <MenuItem key="none" value={null}>
                  <em>{tCommon('All')}</em>
                </MenuItem>
                <MenuItem value={'PAYMENT'}>{tCommon('Payment')}</MenuItem>
                <MenuItem value={'SALE'}>{tCommon('Sale')}</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Customer Groups')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterGroups}
                  onChange={(e) => setFilterGroups(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterGroups) => {
                    if (filterGroups.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterGroups.map((id) => customerGroups.find((cg) => cg.id === id).name);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {customerGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      <Checkbox checked={filterGroups.includes(group.id)} color="primary" />
                      <ListItemText primary={group.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Load Purchases')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generatePaymentHistory}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isShowGenerateButton === null ? (
        <Info title={tReport('Set filters and click generate button')} content={tReport('Reports are generated based on the filters. Please set filters and click generate button to generate the report.')} />
      ) : (
        payments &&
        (paymentsToDisplay && paymentsToDisplay.length ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={paymentsToDisplay}
              summary={{
                date: tCommon('Total'),
                credit: `${shop?.currency || ''} ${summary.totalCredit}`,
                debit: `${shop?.currency || ''} ${summary.totalDebit}`,
                balance: `${shop?.currency || ''} ${summary.totalBalance}`,
              }}
            />
          </div>
        ) : (
          !loadingIndicator && (
            <Info title={tCommon('No payment data to list')} content={tCommon('You haven\'t any payment to list with given properties')} />
          )
        ))
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(CustomerPaymentReport);
